import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import './AppShell.css';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/', icon: '📊' },
  { label: 'Customers', path: '/customers', icon: '👥' },
  { label: 'Licenses', path: '/licenses', icon: '🔐' },
  { label: 'Validation Logs', path: '/validation-logs', icon: '🧾' },
  { label: 'Profile', path: '/profile', icon: '⚙️' }
] as const;

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const currentSection = useMemo(() => {
    return (
      NAV_ITEMS.find((item) =>
        item.path === '/'
          ? location.pathname === '/'
          : location.pathname.startsWith(item.path)
      )?.label ?? 'Dashboard'
    );
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="app-shell">
      <aside className="app-shell__sidebar app-scroll">
        <div className="app-shell__brand">
          <span className="app-shell__brand-mark">VM</span>
          <div>
            <strong>VMGate</strong>
            <div className="app-shell__brand-subtitle">License Control</div>
          </div>
        </div>
        <nav className="app-shell__nav">
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={clsx('app-shell__nav-item', { 'is-active': isActive })}
              >
                <span className="app-shell__nav-icon" aria-hidden>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      <div className="app-shell__main">
        <header className="app-shell__header surface">
          <div>
            <div className="app-shell__section-title">{currentSection}</div>
            <div className="app-shell__section-subtitle">
              Administrative control panel
            </div>
          </div>
          <div className="app-shell__account">
            <div className="app-shell__user">
              <div className="app-shell__user-primary">{user?.full_name ?? user?.username}</div>
              <div className="app-shell__user-secondary">{user?.role ?? ''}</div>
            </div>
            <button className="app-shell__logout" type="button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        <main className="app-shell__content app-scroll">{children}</main>
      </div>
    </div>
  );
}

